import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTurnos } from '../../turnos/hooks/useTurnos';
import { useFiltros } from '../../../shared/hooks/useFiltros';
import { useCatalogos } from '../../calendario/hooks/useCatalogos';
import { turnosAPI } from '../../../core/api/api';
import { MedicalServices } from '@mui/icons-material';
import { useAuth } from '../../../core/context/AuthContext';
import Header from '../../../shared/components/layout/Header';
import Footer from '../../../shared/components/layout/Footer';
import { Alert } from '../../../shared/components';
import { Toast } from '../../../shared/components/Toast';
import FiltrosBusqueda from '../../../shared/components/FiltrosBusqueda';
import TablaPracticas from './TablaPracticas';
import EditarPractica from './EditarPractica/EditarPractica';
import NotificarPracticaModal from './NotificarPracticaModal/NotificarPracticaModal';
import './GrillaDatosPracticas.css';



const GrillaDatosPracticas = () => {
  const { turnos, loading, error, cargarTurnos, getTurnoById, setError } = useTurnos();
  const { filtros, handleFiltroChange, limpiarFiltros } = useFiltros('filtrosPracticas');
  const { centros, tiposEstudios, procesadores, estadosTurno, categorias, obrasSociales, equipos, equiposCPAP } = useCatalogos();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const esMedico = user?.role === 'Medico';
  const [vistaActual, setVistaActual] = useState('grilla');
  const [turnoSeleccionado, setTurnoSeleccionado] = useState(null);
  const [mostrarNotificar, setMostrarNotificar] = useState(false);
  const [turnoNotificar, setTurnoNotificar] = useState(null);
  const [toast, setToast] = useState(null);

  const handleBuscar = () => {
    cargarTurnos(filtros);
  };

  useEffect(() => {
    handleBuscar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  const handleEditarTurno = async (idTurno) => {
    try {
      const turno = await getTurnoById(idTurno);
      setTurnoSeleccionado(turno);
      setVistaActual('editar');
    } catch (err) {
      setError(err.message);
    }
  };

  // Apertura automática de la edición cuando la URL trae ?turno={idTurno}.
  // Sin param → permanece en grilla; param no numérico → permanece en grilla.
  useEffect(() => {
    const turnoParam = searchParams.get('turno');
    if (!turnoParam) return;                       // sin param → grilla
    const idTurno = parseInt(turnoParam, 10);
    if (Number.isNaN(idTurno)) return;             // param inválido → grilla
    abrirEdicionDesdeUrl(idTurno);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apertura de la edición desde la URL con manejo de casos borde:
  // turno inexistente (null/undefined) → grilla + Toast; error/sin permiso → grilla + Toast.
  const abrirEdicionDesdeUrl = async (idTurno) => {
    try {
      const turno = await getTurnoById(idTurno);
      if (!turno) {
        setToast({ message: 'No encontramos el turno indicado.', type: 'error' });
        return;                                    // permanece en grilla
      }
      setTurnoSeleccionado(turno);
      setVistaActual('editar');
    } catch (err) {
      setToast({ message: 'No pudimos abrir el turno. Es posible que no exista o no tengas permiso.', type: 'error' });
    }
  };

  const handleNotificarTurno = async (idTurno) => {
    try {
      const response = await turnosAPI.getTurnoById(idTurno);
      setTurnoNotificar(response.data);
      setMostrarNotificar(true);
    } catch {
      setError('Error al cargar datos del turno para notificar.');
    }
  };

  const handleVolverGrilla = () => {
    setVistaActual('grilla');
    setTurnoSeleccionado(null);
  };

  const handleGuardarTurno = async () => {
    setVistaActual('grilla');
    setTurnoSeleccionado(null);
    await cargarTurnos(filtros);
  };

  if (vistaActual === 'editar' && turnoSeleccionado) {
    return <EditarPractica turno={turnoSeleccionado} onVolver={handleVolverGrilla} onGuardar={handleGuardarTurno} />;
  }

  return (
    <div className="grilla-practicas">
      <Header />
      <div className="main-content">
        <div className="page-header">
          <h2 className="page-title-grilla">
            <MedicalServices sx={{ fontSize: 32, mr: 1, verticalAlign: 'middle', color: '#6d1212' }} />
            Gestión de Prácticas Médicas
          </h2>
          <span className="page-subtitle">Administración y seguimiento de estudios</span>
        </div>

        <Alert message={error} onClose={() => setError('')} />

        <FiltrosBusqueda
          filtros={filtros}
          onFiltroChange={handleFiltroChange}
          onBuscar={handleBuscar}
          onLimpiar={limpiarFiltros}
          centros={centros}
          tiposEstudios={tiposEstudios}
          estadosTurno={estadosTurno}
          procesadores={procesadores}
          categorias={categorias}
          obrasSociales={obrasSociales}
          equipos={equipos}
          equiposCPAP={equiposCPAP}
          loading={loading}
          ocultarProcesador={esMedico}
        />

        <TablaPracticas
          turnos={turnos}
          procesadores={procesadores}
          onEditarTurno={handleEditarTurno}
          onNotificarTurno={handleNotificarTurno}
          loading={loading}
        />
      </div>
      <Footer />
      <NotificarPracticaModal
        show={mostrarNotificar}
        onHide={() => setMostrarNotificar(false)}
        turno={turnoNotificar}
        onResult={(result) => setToast(result)}
      />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default GrillaDatosPracticas;